import { zodResolver } from '@hookform/resolvers/zod';
import omit from 'lodash/omit';
import { JSX } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  Autocomplete,
  FileMimeTypes,
  FilePicker,
  S3FileUploader,
} from '@/modules/common';

import { maxGcrFileSize } from '../constants';
import { GcrFileItem, GcrFormOptions, GcrUploadUrlData } from '../types';
import { GcrFormSchema } from '../validations';

type GcrFormValues = z.infer<typeof GcrFormSchema>;

interface GcrFormProps {
  denseMode?: boolean;
  formClassName?: string;
  options: GcrFormOptions;
  gcrDetails?: GcrFileItem;
  defaultValues?: GcrFormValues;
  uploadUrlData?: GcrUploadUrlData | null;
  refetchUploadCredentials: () => Promise<void>;
  onSubmit: (data: GcrFormValues) => Promise<void>;
  children: (props: { isLoading: boolean }) => JSX.Element;
}

export const defaultFormValues = {
  name: '',
  typeId: '',
  sizeId: '',
  colorId: '',
  printerId: '',
  filePublicUrl: '',
};

export function GcrForm({
  onSubmit,
  children,
  options,
  gcrDetails,
  defaultValues,
  uploadUrlData,
  formClassName = '',
  denseMode = false,
  refetchUploadCredentials,
}: GcrFormProps) {
  const form = useForm<GcrFormValues>({
    resolver: zodResolver(GcrFormSchema),
    defaultValues: defaultValues || defaultFormValues,
  });

  const handleFormSubmit = async () => {
    await onSubmit(omit(form.getValues(), 'filePublicUrl'));
  };

  const filesConfig = [
    {
      key: 'filePublicUrl',
      uploadUrl: uploadUrlData?.url,
      fileUrl: defaultValues?.filePublicUrl,
      fileObject: gcrDetails?.fileObject,
    },
  ];

  const columnClassName = cn(
    'grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6 sm:grid-cols-2 grid-flow-col grid-rows-6 sm:grid-rows-1',
    denseMode && 'grid-cols-2 lg:grid-cols-2 xl:grid-cols-2'
  );

  return (
    <Form {...form}>
      <S3FileUploader
        config={filesConfig}
        onComplete={handleFormSubmit}
        refetchCredentials={refetchUploadCredentials}
      >
        {({
          retryUpload,
          filesState,
          isUploading,
          onFileChange,
          uploadFiles,
        }) => (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              form.handleSubmit(uploadFiles)(event);
            }}
            className={formClassName}
          >
            {children({ isLoading: isUploading })}

            <div className="flex flex-col gap-10 max-w-6xl">
              <div className={columnClassName}>
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>GCR File Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter exact file name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className={columnClassName}>
                <div className="col-span-2">
                  <FilePicker
                    control={form.control}
                    label="GCR File"
                    name="filePublicUrl"
                    accept={[FileMimeTypes.Gcr]}
                    onRetry={retryUpload}
                    isUploading={isUploading}
                    file={filesState.filePublicUrl?.file}
                    error={filesState.filePublicUrl?.error}
                    onFileChange={onFileChange}
                    placeholder="Upload GCR File"
                    tips={
                      <p className="text-sm text-zinc-500">
                        The file must be in
                        <span className="text-zinc-100">&nbsp;.gcr&nbsp;</span>
                        format and maximum size 100 MB.
                      </p>
                    }
                    maxFileSize={maxGcrFileSize}
                  />
                </div>
              </div>

              <div className={columnClassName}>
                <div className="flex flex-col gap-6">
                  <Autocomplete
                    name="typeId"
                    label="Model type"
                    control={form.control}
                    options={options.types}
                    placeholder="Select model type"
                  />

                  <Autocomplete
                    name="sizeId"
                    label="Model size"
                    control={form.control}
                    options={options.sizes}
                    placeholder="Select model size"
                  />
                </div>

                <div className="flex flex-col gap-6">
                  <Autocomplete
                    name="colorId"
                    label="Model color"
                    control={form.control}
                    options={options.colors}
                    placeholder="Select model color"
                  />

                  <Autocomplete
                    name="printerId"
                    label="Printer"
                    control={form.control}
                    options={options.printers}
                    placeholder="Select printer"
                  />
                </div>
              </div>
            </div>
          </form>
        )}
      </S3FileUploader>
    </Form>
  );
}
